/**
 * Date navigation (steppers + calendar + Today + DOC readout) and the cycle
 * fill-map (per-day completion strip, click-to-jump) for the entry surface.
 */
import { useState } from 'react';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  parseISO,
  startOfMonth,
  subDays,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { DayStatus } from '@/lib/waterModel';

const statusBar: Record<DayStatus, string> = {
  complete: 'bg-emerald-400',
  partial: 'bg-amber-400',
  critical: 'bg-red-400',
  empty: 'bg-transparent',
};
const fillSquare: Record<DayStatus, string> = {
  complete: 'bg-emerald-500 text-white',
  partial: 'bg-amber-400 text-white',
  critical: 'bg-red-500 text-white',
  empty: 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
};

interface DateNavProps {
  date: string;
  today: string;
  doc: number | null;
  minDate?: string; // stocking date
  cycleLabel?: string;
  dayStatuses: Map<string, DayStatus>;
  onDate: (date: string) => void;
}

export function DateNav({ date, today, doc, minDate, cycleLabel, dayStatuses, onDate }: DateNavProps) {
  const [calOpen, setCalOpen] = useState(false);
  const cur = parseISO(date);
  const min = minDate ? parseISO(minDate) : undefined;
  const max = parseISO(today);
  const prevDisabled = !!min && !isAfter(cur, min);
  const nextDisabled = !isBefore(cur, max);

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-1">
        <button
          aria-label="Previous day"
          disabled={prevDisabled}
          onClick={() => onDate(format(subDays(cur, 1), 'yyyy-MM-dd'))}
          className="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          aria-label="Next day"
          disabled={nextDisabled}
          onClick={() => onDate(format(addDays(cur, 1), 'yyyy-MM-dd'))}
          className="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="relative">
        <button
          onClick={() => setCalOpen((o) => !o)}
          className="flex items-center gap-2 rounded-lg px-2 py-1 text-left hover:bg-gray-50"
        >
          <CalendarDays className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Entering · {format(cur, 'EEEE')}
            </div>
            <div className="text-sm font-semibold text-gray-900">{format(cur, 'MMM d, yyyy')}</div>
          </div>
        </button>
        {calOpen && (
          <CalendarPopover
            date={date}
            min={min}
            max={max}
            dayStatuses={dayStatuses}
            onPick={(d) => {
              setCalOpen(false);
              onDate(d);
            }}
            onClose={() => setCalOpen(false)}
          />
        )}
      </div>

      <button
        onClick={() => onDate(today)}
        className={cn(
          'rounded-lg px-2.5 py-1 text-sm font-medium',
          date === today ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'
        )}
      >
        Today
      </button>

      <div className="ml-auto flex items-center gap-4 text-right">
        {cycleLabel && <span className="text-sm text-gray-500">{cycleLabel}</span>}
        {doc !== null && (
          <span className="text-sm">
            <span className="text-gray-400">DOC </span>
            <span className="font-semibold text-gray-900">{doc}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function CalendarPopover({
  date,
  min,
  max,
  dayStatuses,
  onPick,
  onClose,
}: {
  date: string;
  min?: Date;
  max: Date;
  dayStatuses: Map<string, DayStatus>;
  onPick: (date: string) => void;
  onClose: () => void;
}) {
  const [viewMonth, setViewMonth] = useState(startOfMonth(parseISO(date)));
  const monthStart = startOfMonth(viewMonth);
  const days = eachDayOfInterval({ start: monthStart, end: endOfMonth(viewMonth) });
  const lead = getDay(monthStart);

  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div className="absolute z-30 mt-1 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <button onClick={() => setViewMonth(addDays(startOfMonth(viewMonth), -1))} className="rounded p-1 hover:bg-gray-100">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium">{format(viewMonth, 'MMMM yyyy')}</span>
          <button onClick={() => setViewMonth(addDays(endOfMonth(viewMonth), 1))} className="rounded p-1 hover:bg-gray-100">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-gray-400">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: lead }).map((_, i) => (
            <div key={`b${i}`} />
          ))}
          {days.map((day) => {
            const ds = format(day, 'yyyy-MM-dd');
            const disabled = (min && isBefore(day, min)) || isAfter(day, max);
            const status = dayStatuses.get(ds);
            return (
              <button
                key={ds}
                disabled={disabled}
                onClick={() => onPick(ds)}
                className={cn(
                  'flex h-7 flex-col items-center justify-center rounded text-xs disabled:opacity-25',
                  ds === date ? 'bg-primary-100 font-semibold text-primary-700' : 'hover:bg-gray-100'
                )}
              >
                {format(day, 'd')}
                <span className={cn('mt-0.5 h-0.5 w-3 rounded', status ? statusBar[status] : 'bg-transparent')} />
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

interface FillMapProps {
  minDate?: string; // stocking date
  today: string;
  date: string;
  dayStatuses: Map<string, DayStatus>;
  onPick: (date: string) => void;
}

export function CycleFillMap({ minDate, today, date, dayStatuses, onPick }: FillMapProps) {
  if (!minDate) return null;
  const start = parseISO(minDate);
  const end = parseISO(today);
  if (isAfter(start, end)) return null;
  const days = eachDayOfInterval({ start, end });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">Days in this crop</span>
        <div className="flex items-center gap-3 text-[10px] text-gray-400">
          {(['complete', 'partial', 'critical', 'empty'] as DayStatus[]).map((s) => (
            <span key={s} className="flex items-center gap-1">
              <span className={cn('h-2.5 w-2.5 rounded-sm', fillSquare[s].split(' ')[0], s === 'empty' && 'border-gray-300')} />
              {s}
            </span>
          ))}
        </div>
      </div>
      {/* p-1 leaves room for the selected day's offset ring, which overflow-x-auto would clip; -mx-1 keeps the boxes aligned with the label. */}
      <div className="-mx-1 flex gap-1 overflow-x-auto p-1">
        {days.map((day, i) => {
          const ds = format(day, 'yyyy-MM-dd');
          const status = dayStatuses.get(ds) ?? 'empty';
          return (
            <button
              key={ds}
              onClick={() => onPick(ds)}
              title={`${format(day, 'MMM d')} · DOC ${i + 1} · ${status}`}
              className={cn(
                'flex h-8 min-w-[26px] shrink-0 items-center justify-center rounded text-[10px] tabular-nums',
                fillSquare[status],
                ds === date && 'font-bold ring-2 ring-primary-500 ring-offset-1'
              )}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}
